import { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CopyId, EmptyState } from '@pazportal/ui';
import { useAuth } from '../contexts/AuthContext';
import { useAuditLog, type AuditLogEntry } from '../hooks/useAuditLog';
import {
  formatAuditAction,
  formatDateTime,
  formatEntityType,
  summarizeAuditState,
} from '../lib/formatters';

function entityHref(type: string | null, id: string | null): string | null {
  if (!type || !id) return null;
  if (type === 'customer' || type === 'customers') return `/customers/${id}`;
  if (type === 'location' || type === 'locations') return `/locations/${id}`;
  if (type === 'ticket' || type === 'tickets') return `/tickets/${id}`;
  if (type === 'field_trip' || type === 'field_trips') return `/trips?trip=${id}`;
  return null;
}

const PAGE_SIZE = 50;
const INPUT =
  'min-h-[44px] md:min-h-[40px] px-3 py-1.5 text-sm border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary';

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function AuditPage() {
  const { role } = useAuth();
  const isSuperadmin = role === 'superadmin';
  const [page, setPage] = useState(0);
  const [entityType, setEntityType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actorSearch, setActorSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const actorQuery = useDebounced(actorSearch.trim(), 300);

  const filters = useMemo(() => ({
    entity_type: entityType || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo ? `${dateTo}T23:59:59` : undefined,
    actor_search: actorQuery || undefined,
    page,
    pageSize: PAGE_SIZE,
  }), [entityType, dateFrom, dateTo, actorQuery, page]);

  const { data, isLoading, isError, error } = useAuditLog(filters, { enabled: isSuperadmin });

  const entries = useMemo(() => data?.entries ?? [], [data?.entries]);
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const from = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, totalCount);

  const scopeLabel = useMemo(() => {
    const parts: string[] = [];
    parts.push(entityType ? `record type ${formatEntityType(entityType)}` : 'all record types');
    if (dateFrom || dateTo) parts.push(`${dateFrom || 'start'} to ${dateTo || 'today'}`);
    if (actorQuery) parts.push(`actor matches "${actorQuery}"`);
    return parts.join(', ');
  }, [entityType, dateFrom, dateTo, actorQuery]);

  const exportCsv = useCallback(() => {
    const headers = ['Timestamp', 'Actor', 'Action', 'Entity Type', 'Entity ID', 'IP', 'Signature'];
    const rows = entries.map((e) => [
      e.created_at,
      e.actor_label,
      e.action,
      e.entity_type ?? '',
      e.entity_id ?? '',
      e.ip_address ? String(e.ip_address) : '',
      e.signature ? 'signed' : 'unsigned',
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const slug = [
      'audit-log',
      entityType || 'all-records',
      dateFrom || dateTo ? `${dateFrom || 'start'}_to_${dateTo || 'today'}` : 'all-dates',
      actorQuery ? `actor-${actorQuery.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}` : null,
      `page-${page + 1}-of-${totalPages}`,
    ]
      .filter(Boolean)
      .join('_');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [entries, entityType, dateFrom, dateTo, actorQuery, page, totalPages]);

  if (!isSuperadmin) {
    return (
      <div className="space-y-3">
        <h1 className="text-xl font-semibold text-text">Audit Log</h1>
        <div role="alert" className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted">
          The audit log is readable by superadmins only (row-level security on <code className="font-mono">audit_log</code>).
          {role ? ` You are signed in as ${role.replace(/_/g, ' ')}.` : ''}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-text">Audit Log</h1>
        <p className="text-sm text-text-muted mt-1">
          Append-only: row-level security permits inserts only, so entries cannot be edited or deleted. The <code className="font-mono">signature</code> column is reserved for HMAC row signing, which is not yet enabled.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-text-muted w-full sm:w-auto">
          Record type
          <select
            value={entityType}
            onChange={(e) => { setEntityType(e.target.value); setPage(0); }}
            className={INPUT}
          >
            <option value="">All records</option>
            <option value="customers">Customer</option>
            <option value="tickets">Ticket</option>
            <option value="deployments">Deployment</option>
            <option value="locations">Location</option>
            <option value="field_trips">Field trip</option>
            <option value="users">User</option>
            <option value="estimates">Estimate</option>
            <option value="unifi_site_map">UniFi site mapping</option>
            <option value="auvik_tenant_map">Auvik tenant mapping</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          From
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            className={INPUT}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          To
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            className={INPUT}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted w-full sm:w-56">
          Actor
          <input
            type="search"
            placeholder="Name or email"
            value={actorSearch}
            onChange={(e) => { setActorSearch(e.target.value); setPage(0); }}
            className={INPUT}
          />
        </label>
        <div className="flex-1" />
        <div className="flex flex-col items-start sm:items-end gap-1">
          <button
            type="button"
            onClick={exportCsv}
            disabled={entries.length === 0}
            className="min-h-[44px] md:min-h-[40px] px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-text-inverse hover:bg-primary-hover disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Export CSV ({entries.length} {entries.length === 1 ? 'row' : 'rows'})
          </button>
          <p className="text-[11px] text-text-faint max-w-xs sm:text-right">
            Exports this page only: {scopeLabel}. The file name carries the same scope.
          </p>
        </div>
      </div>

      {isError && (
        <div role="alert" className="rounded-lg border border-border bg-surface p-4 text-sm text-notification break-words">
          Could not load the audit log{error instanceof Error && error.message ? `: ${error.message}` : ''}.
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-32" role="status" aria-live="polite">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
          <span className="sr-only">Loading audit log</span>
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-2">
            {entries.length === 0 && !isError && (
              <div className="rounded-lg border border-border bg-surface">
                <EmptyState
                  icon={<AuditIcon />}
                  title="No matching activity"
                  subtitle={totalCount === 0 && !entityType && !dateFrom && !dateTo && !actorQuery ? 'Nothing has been written to the audit log yet.' : 'Try a different date range, actor or record type.'}
                />
              </div>
            )}
            {entries.map((entry) => (
              <AuditCard
                key={entry.id}
                entry={entry}
                isExpanded={expandedId === entry.id}
                onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
              />
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-2 text-left">
                  <th className="px-3 py-2.5 w-44">Timestamp</th>
                  <th className="px-3 py-2.5 w-40">Actor</th>
                  <th className="px-3 py-2.5">Action</th>
                  <th className="px-3 py-2.5 w-52">Record</th>
                  <th className="px-3 py-2.5 w-28">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {entries.map((entry) => (
                  <AuditRow
                    key={entry.id}
                    entry={entry}
                    isExpanded={expandedId === entry.id}
                    onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  />
                ))}
                {entries.length === 0 && !isError && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8">
                      <EmptyState
                        icon={<AuditIcon />}
                        title="No matching activity"
                        subtitle={totalCount === 0 && !entityType && !dateFrom && !dateTo && !actorQuery ? 'Nothing has been written to the audit log yet.' : 'Try a different date range, actor or record type.'}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-text-muted">
          <span>Showing {from}–{to} of {totalCount} entries</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="min-h-[44px] md:min-h-[36px] px-3 py-1 rounded-md border border-border disabled:opacity-50 hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >Previous</button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1}
              className="min-h-[44px] md:min-h-[36px] px-3 py-1 rounded-md border border-border disabled:opacity-50 hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditRow({ entry: e, isExpanded, onToggle }: { entry: AuditLogEntry; isExpanded: boolean; onToggle: () => void }) {
  const href = entityHref(e.entity_type, e.entity_id);
  const before = summarizeAuditState(e.before_state);
  const after = summarizeAuditState(e.after_state);

  return (
    <>
      <tr
        className="bg-surface hover:bg-surface-strong transition-colors cursor-pointer"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <td className="px-3 py-2.5 text-xs text-text-muted whitespace-nowrap">{formatDateTime(e.created_at)}</td>
        <td className="px-3 py-2.5 text-text font-medium">{e.actor_label}</td>
        <td className="px-3 py-2.5 text-text">{formatAuditAction(e.action)}</td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            {href ? (
              <Link
                to={href}
                onClick={(event) => event.stopPropagation()}
                className="text-primary hover:underline"
              >
                {formatEntityType(e.entity_type)}
              </Link>
            ) : (
              <span className="text-text-muted">{formatEntityType(e.entity_type)}</span>
            )}
            {e.entity_id && <CopyId value={e.entity_id} />}
          </div>
        </td>
        <td className="px-3 py-2.5 text-xs text-text-faint">{e.ip_address ? String(e.ip_address) : '—'}</td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={5} className="px-3 py-4 bg-surface-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
              <StateList title="Before" rows={before} />
              <StateList title="After" rows={after} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AuditCard({ entry: e, isExpanded, onToggle }: { entry: AuditLogEntry; isExpanded: boolean; onToggle: () => void }) {
  const href = entityHref(e.entity_type, e.entity_id);
  return (
    <div className="rounded-lg border border-border bg-surface">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="w-full text-left p-3 min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
      >
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-text break-words">{e.actor_label}</p>
          <span className="text-xs text-text-faint whitespace-nowrap">{formatDateTime(e.created_at)}</span>
        </div>
        <p className="text-sm text-text mt-0.5 break-words">{formatAuditAction(e.action)}</p>
        <p className="text-xs text-text-muted mt-1">
          {formatEntityType(e.entity_type)}
          {e.ip_address ? ` · ${String(e.ip_address)}` : ''}
        </p>
      </button>
      {isExpanded && (
        <div className="border-t border-border p-3 space-y-3 bg-surface-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {href && (
              <Link to={href} className="text-primary hover:underline">Open record</Link>
            )}
            {e.entity_id && <CopyId value={e.entity_id} />}
          </div>
          <StateList title="Before" rows={summarizeAuditState(e.before_state)} />
          <StateList title="After" rows={summarizeAuditState(e.after_state)} />
        </div>
      )}
    </div>
  );
}

function StateList({ title, rows }: { title: string; rows: Array<{ label: string; value: string }> }) {
  return (
    <div>
      <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-text-faint">No field changes to show.</p>
      ) : (
        <dl className="space-y-1.5">
          {rows.map((row) => (
            <div key={row.label} className="flex justify-between gap-3 text-sm">
              <dt className="text-text-muted capitalize">{row.label}</dt>
              <dd className="text-text text-right break-words max-w-[16rem]">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function AuditIcon() {
  return (
    <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
